import { useTranslation } from 'react-i18next';

import {
  ColumnConfig,
  TableColumnHeader,
} from '@/common/components/table/TableColumnHeader';
import { TableHeader, TableRow } from '@/common/components/ui/table';
import { PolicyFilters } from '@/policy/api/FetchPolicyList';
import { PolicySortField, SortOrder } from '@/policy/types/SortTypes';

interface PolicyHeaderProps {
  sortBy: PolicySortField;
  sortOrder: SortOrder;
  filters: PolicyFilters;
  onSort: (field: PolicySortField) => void;
  onFilterChange: (field: string, value: string | string[]) => void;
}

export const PolicyListHeader = ({
  sortBy,
  sortOrder,
  filters,
  onSort,
  onFilterChange,
}: PolicyHeaderProps) => {
  const { t } = useTranslation();

  const columns: ColumnConfig<PolicySortField>[] = [
    {
      field: 'createdAt',
      label: t('policy.spill'),
      sortable: true,
      filterable: false,
      width: 'min-w-[100px]',
    },
    {
      field: 'accountName',
      label: t('policy.accountName'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'accountName',
      filterPlaceholder: t('policy.filter.accountName'),
      width: 'min-w-[125px]',
    },
    {
      field: 'fileName',
      label: t('policy.fileName'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'fileName',
      filterPlaceholder: t('policy.filter.fileName'),
      width: 'min-w-[125px]',
    },
    {
      field: 'policyNumber',
      label: t('policy.policyNumber'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'policyNumber',
      filterPlaceholder: t('policy.filter.policyNumber'),
      width: 'min-w-[125px]',
    },
    {
      field: 'insurer',
      label: t('policy.insurer'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'insurer',
      filterPlaceholder: t('policy.filter.insurer'),
      width: 'min-w-[125px]',
    },
    {
      field: 'expiryDate',
      label: t('policy.expiryDate'),
      sortable: true,
      filterable: false,
      width: 'min-w-[125px]',
    },
    {
      field: 'technicalSupport',
      label: t('policy.technicalSupport'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      filterKey: 'technicalSupport',
      filterPlaceholder: t('policy.filter.technicalSupport'),
      width: 'min-w-[125px]',
    },
    {
      field: 'status',
      label: t('policy.status'),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterKey: 'status',
      filterPlaceholder: t('policy.filter.status'),
      options: [
        { value: '', label: t('policy.filter.allStatus') },
        { value: 'PENDING_VALIDATION', label: t('PENDING_VALIDATION') },
        { value: 'IN_PROGRESS', label: t('IN_PROGRESS') },
        { value: 'NOT_COMPLETED', label: t('NOT_COMPLETED') },
        { value: 'COMPLETED', label: t('COMPLETED') },
      ],
      width: 'min-w-[150px]',
    },
    {
      field: null,
      label: t('policy.assignation'),
      sortable: false,
      filterable: false,
      width: 'min-w-[100px]',
    },
    {
      field: 'validatedAt',
      label: t('policy.validationDate'),
      sortable: true,
      filterable: false,
      width: 'min-w-[125px]',
    },
    {
      field: null,
      label: '',
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <TableHeader className="sticky top-0 z-10 bg-background">
      <TableRow>
        {columns.map((column) => (
          <TableColumnHeader
            key={column.label}
            column={column}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSort={onSort}
            onFilterChange={onFilterChange}
          />
        ))}
      </TableRow>
    </TableHeader>
  );
};
